import { z } from 'zod';

export const RegisterSchema = z.object({
  device_id: z.string().min(8).max(128),
  display_name: z.string().min(1).max(60),
});

export const RefreshSchema = z.object({
  device_id: z.string().min(8).max(128),
});

export const CreateCircleSchema = z.object({
  name: z.string().min(1).max(60).optional(),
});

export const RenameCircleSchema = z.object({
  name: z.string().min(1).max(40),
});

export const JoinCircleSchema = z.object({
  invite_code: z.string().min(6).max(24),
});

export const SendCardSchema = z.object({
  card_template_id: z.string().uuid(),
  circle_id: z.string().uuid(),
  intensity: z.enum(['soft', 'firm']),
  expires_at: z.string().datetime().optional(),
});

export const AckCardSchema = z.object({
  response: z.string().min(1).max(120).optional(),
});

const NotificationSettingsSchema = z.object({
  push_enabled: z.boolean(),
  soft_behaviour: z.enum(['badge', 'visible', 'off']),
  firm_behaviour: z.enum(['visible_sound', 'visible_silent', 'badge', 'off']),
  re_nudge_enabled: z.boolean(),
  re_nudge_delay_minutes: z.union([z.literal(10), z.literal(15), z.literal(30), z.literal(60)]),
  re_nudge_max: z.union([z.literal(0), z.literal(1), z.literal(2), z.literal(3)]),
}).partial();

const QuietHoursSchema = z.object({
  enabled: z.boolean(),
  start: z.string().regex(/^\d{2}:\d{2}$/),
  end: z.string().regex(/^\d{2}:\d{2}$/),
}).nullable();

export const UserSettingsPartialSchema = z.object({
  theme: z.enum(['dark', 'light', 'system']),
  reduce_motion: z.boolean(),
  high_contrast: z.boolean(),
  font_size: z.enum(['default', 'large', 'xlarge']),
  card_size: z.enum(['compact', 'comfortable', 'large']),
  haptic_feedback: z.boolean(),
  sound_enabled: z.boolean(),
  notifications: NotificationSettingsSchema,
  quiet_hours: QuietHoursSchema,
  partner_display_name: z.string().nullable(),
  first_card_at: z.string().nullable(),
  bmac_prompt_shown: z.boolean(),
}).partial();

export const UpdateUserSchema = z.object({
  display_name: z.string().min(1).max(60).optional(),
  avatar_url: z.string().url().nullable().optional(),
  settings: UserSettingsPartialSchema.optional(),
});

export const CreateCardTemplateSchema = z.object({
  circle_id: z.string().uuid(),
  category: z.string().min(1).max(40),
  icon: z.string().min(1).max(8),
  label: z.string().min(1).max(30),
  subtext: z.string().max(200).optional(),
  colour: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
  is_shared: z.boolean(),
});

export const UpdateCardTemplateSchema = z.object({
  label: z.string().min(1).max(30).optional(),
  subtext: z.string().max(200).nullable().optional(),
  icon: z.string().min(1).max(8).optional(),
  colour: z.string().regex(/^#[0-9a-fA-F]{6}$/).nullable().optional(),
  category: z.string().min(1).max(40).optional(),
  is_shared: z.boolean().optional(),
});

export const ReorderCardsSchema = z.object({
  updates: z.array(z.object({
    id: z.string().uuid(),
    sort_order: z.number().int().nonnegative(),
  })).min(1),
});

export const PushSubscribeSchema = z.object({
  endpoint: z.string().url(),
  keys: z.object({
    p256dh: z.string().min(1),
    auth: z.string().min(1),
  }),
});

export const PushUnsubscribeSchema = z.object({
  endpoint: z.string().url(),
});

export const OAuthSchema = z.object({
  provider: z.enum(['google', 'apple']),
  credential: z.string().min(8),
  device_id: z.string().min(8).max(128).optional(),
  display_name: z.string().min(1).max(60).optional(),
});
